import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { tours, categories } from '../../mocks/tours';

export default function ToursPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');

  const filtered = tours
    .filter((t) => activeCategory === 'All' || t.category === activeCategory)
    .sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return b.reviews - a.reviews;
    });

  return (
    <div>
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-72 mt-0 pt-0 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/c2ac6e743ea0d8a2a110c2ced87f309f.jpg"
          alt="Tour Packages"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-emerald-900/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pt-20">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-widest mb-3">Your Adventure Awaits</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-playfair">Tour Packages</h1>
          <p className="text-white/75 mt-3 text-base max-w-xl">
            Discover our wide range of meticulously crafted tour packages to destinations around the globe.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-emerald-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:border-emerald-400 cursor-pointer"
            >
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Tour Grid */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-gray-500 text-sm mb-8">
            Showing <strong className="text-gray-900">{filtered.length}</strong> packages
            {activeCategory !== 'All' && <span> in <strong className="text-emerald-700">{activeCategory}</strong></span>}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((tour) => (
              <div
                key={tour.id}
                className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-emerald-200 transition-all duration-300 cursor-pointer"
                data-product-shop
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={tour.image}
                    alt={tour.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  {tour.tag && (
                    <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {tour.tag}
                    </span>
                  )}
                  {tour.originalPrice && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      SALE
                    </span>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-emerald-700 bg-emerald-50 text-xs font-medium px-2 py-0.5 rounded-full">{tour.category}</span>
                    <span className="text-gray-400 text-xs">{tour.duration}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-2 leading-snug font-playfair line-clamp-2">{tour.title}</h3>

                  <div className="flex items-center gap-1 mb-3">
                    <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                      <i className="ri-star-fill text-xs"></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{tour.rating}</span>
                    <span className="text-gray-400 text-xs">({tour.reviews})</span>
                  </div>

                  <div className="space-y-1 mb-4">
                    {tour.inclusions.slice(0, 3).map((inc) => (
                      <div key={inc} className="flex items-center gap-1.5 text-gray-500 text-xs">
                        <div className="w-3 h-3 flex items-center justify-center text-emerald-500 flex-shrink-0">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        {inc}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-end justify-between pt-3 border-t border-gray-100">
                    <div>
                      {tour.originalPrice && (
                        <p className="text-gray-400 text-xs line-through">RM {tour.originalPrice.toLocaleString()}</p>
                      )}
                      <p className="text-emerald-700 font-bold text-lg">
                        RM {tour.price.toLocaleString()}
                        <span className="text-gray-400 text-xs font-normal ml-1">/ pax</span>
                      </p>
                    </div>
                    <button className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                      Enquire Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-search-line text-4xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-500">No packages found for this category.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
